import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRisingEdgeEffect } from 'src/clientLib/hooks'
import { useNavigationEffect } from 'src/redux/slices/navigation/hooks'
import { getProject } from 'src/redux/slices/project/endpoints'
import { Entrypoint, KeyFields } from 'src/redux/slices/umibuilder/consts'
import {
  confirmKeyFields,
  exportExchangeFile,
  exportKeyFieldsErrorReport,
  mutationFixedCacheKeys
} from 'src/redux/slices/umibuilder/endpoints'
import {
  computeErrors,
  setKeyFieldAssignment
} from 'src/redux/slices/umibuilder/reducers'
import {
  allFieldsAssigned,
  selectClimateZones,
  selectEntrypoint,
  selectErrorSummary,
  selectGeoJSON,
  selectGroupData,
  selectKeyFields,
  selectUmiBuilderFromCloud
} from 'src/redux/slices/umibuilder/selectors'

// MUI
import Button from '@material-ui/core/Button'
import Divider from '@material-ui/core/Divider'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { skipToken } from '@reduxjs/toolkit/query/react'

// Components
import DisplayGISMap from '../components/DisplayGISMap'
import KeyFieldSelector from '../components/KeyFieldSelector'
import KeyFieldsErrorButton from '../components/KeyFieldsErrorButton'
import KeyfieldsErrorBypassModal from '../components/KeyfieldsErrorBypassModal'

///// Component
const KeyfieldsPage: React.FC = () => {
  const dispatch = useDispatch()
  const fromCloud = useSelector(selectUmiBuilderFromCloud)
  const entrypoint = useSelector(selectEntrypoint)
  const geoJSON = useSelector(selectGeoJSON)
  const assignments = useSelector(selectKeyFields)
  const canProceed = useSelector(allFieldsAssigned)
  const hasGroupData = useSelector(selectGroupData)
  const errorSummary = useSelector(selectErrorSummary)
  const climateZones = useSelector(selectClimateZones)
  const { data: project } = getProject.useQuery('')

  const [triggerExportExchange, { isLoading: isExportingExchange }] =
    exportExchangeFile.useMutation({
      fixedCacheKey: mutationFixedCacheKeys.exportExchangeFile,
    })
  const [triggerExportErrorReport, { isLoading: isExportingErrors }] =
    exportKeyFieldsErrorReport.useMutation({
      fixedCacheKey: mutationFixedCacheKeys.exportKeyFieldsErrorReport,
    })
  const [triggerConfirmKeyFields, { isLoading: isConfirmingKeyFields }] =
    confirmKeyFields.useMutation({
      fixedCacheKey: mutationFixedCacheKeys.confirmKeyFields,
    })

  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const openConfirmModal = useCallback(() => {
    setShowConfirmModal(true)
  }, [setShowConfirmModal])

  const closeConfirmModal = useCallback(() => {
    setShowConfirmModal(false)
  }, [setShowConfirmModal])

  const confirmKeyFieldsAction = useCallback(() => {
    const keyFields = {
      ...assignments,
    }
    if (errorSummary.id > 0 || errorSummary.duplicateId > 0) {
      keyFields.id = 'N/A'
      dispatch(setKeyFieldAssignment({ field: 'id', value: 'N/A' }))
    }
    const body = {
      keyFields,
      fromCloud,
      project_id: project?.id,
    }
    triggerConfirmKeyFields(body)
  }, [assignments, dispatch, errorSummary.id, errorSummary.duplicateId, fromCloud, project?.id])

  const exportExchangeFileAction = useCallback(() => {
    triggerExportExchange({ assignments })
  }, [assignments, triggerExportExchange])

  const handleConfirm = useCallback(() => {
    if (errorSummary.any > 0) {
      openConfirmModal()
      return
    }
    switch (entrypoint) {
      case Entrypoint.gis:
        exportExchangeFileAction()
        break
      default:
        confirmKeyFieldsAction()
        break
    }
  }, [
    entrypoint,
    confirmKeyFieldsAction,
    exportExchangeFileAction,
    openConfirmModal,
    errorSummary.any,
  ])

  const handleContinue = useCallback(() => {
    switch (entrypoint) {
      case Entrypoint.gis:
        exportExchangeFileAction()
        break
      default:
        confirmKeyFieldsAction()
        break
    }
  }, [
    entrypoint,
    confirmKeyFieldsAction,
    exportExchangeFileAction,
    openConfirmModal,
    errorSummary,
    dispatch,
  ])

  const exportErrorReport = useCallback(() => {
    triggerExportErrorReport({ geoJSON })
  }, [geoJSON, triggerExportErrorReport])

  // Entrypoint not a dependency since we only want this runnin on first load
  useEffect(() => {
    if (entrypoint === Entrypoint.uio) {
      if (!hasGroupData) {
        dispatch(computeErrors())
        // confirmKeyFields()
      }
    }
  }, [dispatch, confirmKeyFieldsAction, hasGroupData])

  // If the mode changes to UIO, automatically confirm keyfields
  useRisingEdgeEffect({
    callback: confirmKeyFieldsAction,
    condition: entrypoint !== Entrypoint.gis,
  })
  // go to organize page on hasGroupData's rising edge
  useNavigationEffect({ destination: 'organize', condition: hasGroupData })

  const [mapColorMode, setMapColorMode] = useState('anyErrors')

  return (
    <Grid container spacing={3} justifyContent='center'>
      <Grid
        item
        xs={12}
        md={6}
        container
        justifyContent='center'
        alignItems='center'
        spacing={5}
        direction='column'
      >
        <Typography variant='caption'>
          Select fields based on your GIS attributes to continue.
        </Typography>
        {KeyFields.map((keyField, i) => (
          <Grid
            item
            container
            justifyContent='center'
            alignItems='center'
            spacing={2}
            key={`keyfield-${keyField}-${i}-selector`}
          >
            <Grid item xs>
              <KeyFieldSelector keyField={keyField} />
            </Grid>
            <Grid item>
              <KeyFieldsErrorButton keyField={keyField} setMapColorMode={setMapColorMode} />
            </Grid>
          </Grid>
        ))}
        <Grid item container spacing={3} justifyContent='space-around'>
          <Button
            onClick={() => setMapColorMode('overlapError')}
            color='secondary'
            size='small'
            disabled={errorSummary.overlap === 0}
            variant='contained'
          >
            Show Overlap Errors
          </Button>
          <Button
            onClick={() => setMapColorMode('anyErrors')}
            size='small'
            disabled={errorSummary.any === 0}
            variant='contained'
          >
            Show All
          </Button>
          <Button
            onClick={exportErrorReport}
            size='small'
            disabled={
              Object.values(errorSummary).filter((a) => a > 0).length === 0 || isExportingErrors
            }
            variant='contained'
          >
            Download Error Report
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <DisplayGISMap mapColorMode={mapColorMode} />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid item xs={12} container justifyContent='center' alignItems='center'>
        {entrypoint !== Entrypoint.uio && !fromCloud && (
          <Button
            size='large'
            onClick={handleConfirm}
            disabled={!canProceed || isExportingExchange}
            variant='contained'
          >
            Export UIO
          </Button>
        )}
        {(entrypoint !== Entrypoint.gis || fromCloud) && (
          <Button
            size='large'
            onClick={handleConfirm}
            disabled={!canProceed || isConfirmingKeyFields}
            variant='contained'
          >
            Confirm
          </Button>
        )}
        {climateZones && (
          <Grid item xs={12} container justifyContent='center' alignItems='center'>
            <Typography variant='overline'>Climate Zone: {climateZones.join(',')}</Typography>
          </Grid>
        )}
      </Grid>
      <KeyfieldsErrorBypassModal
        open={showConfirmModal}
        handleClose={closeConfirmModal}
        handleContinue={handleContinue}
        // exportErrorReport={exportErrorReport}
      />
    </Grid>
  )
}

export default KeyfieldsPage
